'use client';

import { ActionParamPopover } from '@/components/chat/action-param-popover';
import { AttachmentCapStatus } from '@/components/chat/attachment-cap-status';
import { ComposerAttachmentToast } from '@/components/chat/composer-attachment-toast';
import { ComposerSendButton } from '@/components/chat/composer-send-button';
import { HelpModal } from '@/components/chat/help-modal';
import { StickyThreadAttachments } from '@/components/chat/sticky-thread-attachments';
import { useActionParamStore } from '@/lib/action-param-store';
import { commandById, listMentionTools, listSlashActions } from '@/lib/command-meta';
import { env } from '@/lib/env';
import { useHelpModalStore } from '@/lib/help-modal-store';
import { runChatAction } from '@/lib/run-action';
import { unstable_defaultDirectiveFormatter } from '@assistant-ui/core';
import {
  ActionBarMorePrimitive,
  ActionBarPrimitive,
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  type Unstable_SlashCommand,
  unstable_useMentionAdapter,
  unstable_useSlashCommandAdapter,
  useAuiState,
  useThreadRuntime,
} from '@assistant-ui/react';
import { type DirectiveChipProps, LexicalComposerInput } from '@assistant-ui/react-lexical';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Textarea,
  cn,
} from '@iq/ui';
import {
  ArrowDownIcon,
  BookOpenIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudSunIcon,
  CopyIcon,
  DownloadIcon,
  FileTextIcon,
  GlobeIcon,
  HelpCircleIcon,
  LanguagesIcon,
  MoreHorizontalIcon,
  PencilIcon,
  RefreshCwIcon,
  SlashIcon,
  SquareIcon,
  TriangleAlertIcon,
  WrenchIcon,
} from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CitationFooter } from '../chat/citation-footer';
import { ComposerAddAttachment, ComposerAttachments, UserMessageAttachments } from './attachment';
import { ComposerTriggerPopover } from './composer-trigger-popover';
import { createDirectiveText } from './directive-text';
import { MarkdownText } from './markdown-text';
import { MessageTiming } from './message-timing';
import { ComposerQuotePreview, QuoteBlock, SelectionToolbar } from './quote';
import {
  Reasoning,
  ReasoningContent,
  ReasoningRoot,
  ReasoningText,
  ReasoningTrigger,
} from './reasoning';
import { ToolFallback } from './tool-fallback';
import { ToolGroupContent, ToolGroupRoot, ToolGroupTrigger } from './tool-group';
import { TooltipIconButton } from './tooltip-icon-button';

export const Thread: FC = () => {
  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
      style={{
        ['--thread-max-width' as string]: '44rem',
        ['--composer-radius' as string]: '24px',
        ['--composer-padding' as string]: '10px',
      }}
    >
      {/* Composer slash-command satellites — see Composer + COMMAND_META. */}
      <HelpModal />
      <ActionParamPopover />
      {/* Routes composer.attachmentAddError → localized sonner toast. */}
      <ComposerAttachmentToast />
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        data-slot="aui_thread-viewport"
        // `aui-thin-scrollbar` styles the native scrollbar (thin, accent
        // thumb, no track gutter). See globals.css — purely cosmetic, no
        // layout impact.
        className="aui-thin-scrollbar relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth px-4 pt-4"
      >
        <AuiIf condition={(s) => s.thread.isEmpty}>
          <ThreadWelcome />
        </AuiIf>

        <div data-slot="aui_message-group" className="mb-10 flex flex-col gap-y-8 empty:hidden">
          <ThreadPrimitive.Messages>
            {({ message }) => {
              if (message.composer.isEditing) return <EditComposer />;
              if (message.role === 'user') return <UserMessage />;
              return <AssistantMessage />;
            }}
          </ThreadPrimitive.Messages>
          <AuiIf condition={(s) => s.thread.isRunning}>
            <ThreadLoadingIndicator />
          </AuiIf>
        </div>

        <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-(--thread-max-width) flex-col gap-4 overflow-visible rounded-t-(--composer-radius) bg-background pt-2 pb-4 md:pt-3 md:pb-6">
          <ThreadScrollToBottom />
          <ThreadStickyAttachmentsStrip />
          <Composer />
          <AttachmentCapStatus />
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>

      <SelectionToolbar />
    </ThreadPrimitive.Root>
  );
};

/**
 * Per-thread sticky-attachment strip. Reads the active thread's remoteId
 * from the AUI store (we cannot pass it as a prop here without rewriting
 * `Thread` to accept it — and the runtime hops between threads in-place
 * via `useRemoteThreadListRuntime`) and renders the chip list. When the
 * active thread is a draft (`remoteId` undefined) the strip itself
 * returns null and renders nothing.
 */
const ThreadStickyAttachmentsStrip: FC = () => {
  const remoteId = useAuiState((s) => s.threadListItem.remoteId) as string | undefined;
  return <StickyThreadAttachments threadId={remoteId ?? null} />;
};

const ThreadLoadingIndicator: FC = () => {
  const { t } = useTranslation();
  return (
    <div
      data-slot="aui_loading-indicator"
      role="status"
      aria-live="polite"
      aria-label={t('chat.loading.responding')}
      className="fade-in slide-in-from-bottom-1 mx-auto flex w-full max-w-(--thread-max-width) animate-in items-center gap-2 px-4 text-muted-foreground text-sm duration-150"
    >
      <span className="inline-flex items-center gap-1" aria-hidden>
        <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
        <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
        <span className="size-1.5 animate-bounce rounded-full bg-current" />
      </span>
      <span>{t('chat.loading.thinking')}</span>
    </div>
  );
};

const ThreadScrollToBottom: FC = () => {
  const { t } = useTranslation();
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip={t('chat.composer.scrollToBottom')}
        variant="outline"
        className="aui-thread-scroll-to-bottom absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible dark:border-border dark:bg-background dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  const { t } = useTranslation();
  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-(--thread-max-width) grow flex-col">
      <div className="aui-thread-welcome-center flex w-full grow flex-col items-center justify-center">
        <div className="aui-thread-welcome-message flex size-full flex-col justify-center px-4">
          <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both font-semibold text-2xl duration-200">
            {t('chat.welcome.title')}
          </h1>
          <p className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-muted-foreground text-xl delay-75 duration-200">
            {t('chat.welcome.subtitle')}
          </p>
        </div>
      </div>
      <ThreadSuggestions />
    </div>
  );
};

// The welcome suggestions are rendered INLINE with `ThreadPrimitive.Suggestion`
// (driven by `t()`) rather than through the runtime `Suggestions()` data API.
// The runtime path snapshots its suggestion strings when the resource is first
// created and does not re-read them when `i18n.changeLanguage()` fires, so the
// cards stayed English after a UI-language switch. Rendering them here makes
// them re-localize reactively, exactly like the welcome heading above.
const WELCOME_SUGGESTIONS = [
  {
    key: 'weather',
    titleKey: 'chat.suggestions.weather.title',
    descriptionKey: 'chat.suggestions.weather.description',
    promptKey: 'chat.suggestions.weather.prompt',
  },
  {
    key: 'troubleshoot',
    titleKey: 'chat.suggestions.troubleshoot.title',
    descriptionKey: 'chat.suggestions.troubleshoot.description',
    promptKey: 'chat.suggestions.troubleshoot.prompt',
  },
] as const;

const ThreadSuggestions: FC = () => {
  const { t } = useTranslation();
  return (
    <div className="aui-thread-welcome-suggestions grid w-full @md:grid-cols-2 gap-2 pb-4">
      {WELCOME_SUGGESTIONS.map((s) => (
        <div
          key={s.key}
          className="aui-thread-welcome-suggestion-display fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200"
        >
          <ThreadPrimitive.Suggestion prompt={t(s.promptKey)} send asChild>
            <Button
              variant="ghost"
              className="aui-thread-welcome-suggestion h-auto w-full @md:flex-col flex-wrap items-start justify-start gap-1 rounded-3xl border bg-background px-4 py-3 text-left text-sm transition-colors hover:bg-muted"
            >
              <span className="aui-thread-welcome-suggestion-text-1 font-medium">
                {t(s.titleKey)}
              </span>
              <span className="aui-thread-welcome-suggestion-text-2 text-muted-foreground empty:hidden">
                {t(s.descriptionKey)}
              </span>
            </Button>
          </ThreadPrimitive.Suggestion>
        </div>
      ))}
    </div>
  );
};

// Lucide ref-forward components advertise `className?: string | undefined`,
// which isn't assignable to FC<{className?: string}> under
// `exactOptionalPropertyTypes`. Wrap each icon so the prop shape matches.
type IconLike = FC<{ className?: string }>;
const wrap = (Icon: typeof FileTextIcon): IconLike => {
  const Wrapped: IconLike = ({ className }) => <Icon className={className} />;
  Wrapped.displayName = `WrappedIcon(${Icon.displayName ?? 'icon'})`;
  return Wrapped;
};
const FileTextIconW = wrap(FileTextIcon);
const LanguagesIconW = wrap(LanguagesIcon);
const GlobeIconW = wrap(GlobeIcon);
const HelpCircleIconW = wrap(HelpCircleIcon);
const CloudSunIconW = wrap(CloudSunIcon);
const BookOpenIconW = wrap(BookOpenIcon);
const SlashIconW = wrap(SlashIcon);
const WrenchIconW = wrap(WrenchIcon);

/**
 * `Text` message-part component for user messages with directive icons.
 *
 * The composer renders `:tool[Label]{name=id}` directives via the
 * `unstable_useMentionAdapter`, and the popover shows a wrench icon next to
 * each candidate. Without `createDirectiveText` wired here the SAME chip in
 * the user's persisted message renders icon-less — inconsistent UX. The
 * factory replaces the default `DirectiveText` with one that maps the
 * `tool` directive type to the wrench icon, matching the composer.
 *
 * Today only the `tool` directive type reaches text (slash actions execute
 * via popover/modal and never serialize). `fallbackIcon` is set so any
 * future directive type still gets an icon while we wait for a richer map.
 */
const DirectiveText = createDirectiveText(unstable_defaultDirectiveFormatter, {
  // Each key matches the `type` segment of the serialized directive
  // (`:type[Label]{name=id}`). `tool` covers the `@` mention path the
  // `unstable_useMentionAdapter` serializes; `summarize` / `translate`
  // cover the `/` slash-action path that `lib/run-action.ts` emits when
  // the user picks an action — same icons as the composer palette so the
  // sent bubble and the popover row stay visually consistent.
  iconMap: {
    tool: WrenchIconW,
    summarize: FileTextIconW,
    translate: LanguagesIconW,
  },
  fallbackIcon: WrenchIconW,
});

/**
 * Maps the icon name we emit in each slash-command entry (see Composer
 * below) to the wrapped Lucide component the assistant-ui adapter renders
 * in the palette row. New tools land here.
 */
const slashIconMap: Record<string, IconLike> = {
  CloudSun: CloudSunIconW,
  Globe: GlobeIconW,
  BookOpen: BookOpenIconW,
  FileText: FileTextIconW,
  Languages: LanguagesIconW,
  HelpCircle: HelpCircleIconW,
};

/**
 * Friendly name for a Lucide icon component used in `COMMAND_META`.
 * The icon's React `displayName` is reliable for the lucide-react v6
 * series; we strip the trailing `Icon` so it matches the `slashIconMap`
 * keys above.
 */
function iconKeyForCommand(icon: unknown): string {
  const fn = icon as { displayName?: string; name?: string };
  const raw = fn.displayName ?? fn.name ?? 'Wrench';
  return raw.endsWith('Icon') ? raw.slice(0, -'Icon'.length) : raw;
}

function DirectiveChip(props: DirectiveChipProps) {
  const { directiveId, directiveType, label } = props;
  const { t } = useTranslation();
  const showWrench = directiveType !== 'command';
  // For known slash-commands, render the localised label (e.g. "Web Search")
  // instead of the raw directive id. Unknown directives fall back to the
  // adapter-supplied label.
  const meta = commandById(directiveId);
  const formatted = meta ? t(meta.labelKey) : label;
  return (
    <span
      className="aui-directive-chip"
      data-directive-type={directiveType}
      data-directive-id={directiveId}
    >
      {showWrench && (
        <span className="aui-directive-chip-icon">
          <WrenchIcon className="size-3" />
        </span>
      )}
      <span className="aui-directive-chip-label">{formatted}</span>
    </span>
  );
}

const Composer: FC = () => {
  const { t } = useTranslation();
  const openHelpModal = useHelpModalStore((s) => s.openModal);
  const openActionParam = useActionParamStore((s) => s.open);
  // `useThreadRuntime` gives us the imperative `append` + `composer.setText`
  // pair `runChatAction` needs. Stable per-thread; safe to read on every
  // render without a memo.
  const threadRuntime = useThreadRuntime();

  // Tools live behind `@` (mention palette) — mirrors the shadcn assistant-ui
  // example. We pass explicit `items` because our toolkit's `type: 'backend'`
  // tools cannot carry a `description` field through assistant-ui's
  // auto-discovery (BackendTool.description?: undefined). The ComposerTrigger
  // Popover renders `item.description` directly under `item.label`.
  const mentionItems = listMentionTools().map((m) => ({
    id: m.id,
    type: 'tool',
    label: t(m.labelKey),
    description: t(m.descriptionKey),
    icon: iconKeyForCommand(m.icon),
  }));

  // Action commands live behind `/`.
  //
  // Three branches: `help` opens its own modal; `summarize` short-circuits
  // through `runChatAction` with the default `medium` length (no dialog —
  // user explicitly removed the length picker, see plan.md); `translate`
  // still opens the action-param dialog so the user can pick the target
  // language. The slash-popover's `unstable_useSlashCommandAdapter` calls
  // `execute` after the palette selection, so any composer-side cleanup
  // (clearing the inserted chip) happens inside `runChatAction` rather
  // than here.
  const slashCommands: readonly Unstable_SlashCommand[] = listSlashActions().map((m) => ({
    id: m.id,
    description: t(m.descriptionKey),
    icon: iconKeyForCommand(m.icon),
    execute: () => {
      if (m.id === 'help') {
        openHelpModal();
        return;
      }
      if (m.id === 'summarize') {
        runChatAction(threadRuntime, { kind: 'summarize', length: 'medium' });
        return;
      }
      if (m.id === 'translate') {
        openActionParam('translate');
      }
    },
  }));

  const mention = unstable_useMentionAdapter({
    items: mentionItems,
    iconMap: slashIconMap,
    fallbackIcon: WrenchIconW,
    includeModelContextTools: false,
  });
  const slash = unstable_useSlashCommandAdapter({
    commands: slashCommands,
    iconMap: slashIconMap,
    fallbackIcon: SlashIconW,
  });

  return (
    <ComposerPrimitive.Unstable_TriggerPopoverRoot>
      <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
        <ComposerPrimitive.AttachmentDropzone asChild>
          <div
            data-slot="aui_composer-shell"
            className="flex w-full flex-col gap-2 rounded-(--composer-radius) border bg-background p-(--composer-padding) transition-shadow focus-within:border-ring/75 focus-within:ring-2 focus-within:ring-ring/20 data-[dragging=true]:border-ring data-[dragging=true]:border-dashed data-[dragging=true]:bg-accent/50"
          >
            <ComposerQuotePreview />
            <ComposerAttachments />
            <LexicalComposerInput
              directiveChip={DirectiveChip}
              placeholder={t('chat.composer.placeholder')}
              className="aui-composer-input relative max-h-32 min-h-10 w-full resize-none bg-transparent px-1.75 py-1 text-sm outline-none [&_.aui-directive-chip-icon]:self-center [&_.aui-directive-chip]:inline-flex [&_.aui-directive-chip]:items-baseline [&_.aui-directive-chip]:gap-1 [&_.aui-directive-chip]:rounded-md [&_.aui-directive-chip]:bg-blue-100 [&_.aui-directive-chip]:px-1.5 [&_.aui-directive-chip]:py-0.5 [&_.aui-directive-chip]:font-medium [&_.aui-directive-chip]:text-[13px] [&_.aui-directive-chip]:text-blue-700 [&_.aui-directive-chip]:leading-none dark:[&_.aui-directive-chip]:bg-blue-900/50 dark:[&_.aui-directive-chip]:text-blue-300 [&_.aui-lexical-input]:min-h-lh [&_.aui-lexical-input]:outline-none [&_.aui-lexical-placeholder]:pointer-events-none [&_.aui-lexical-placeholder]:absolute [&_.aui-lexical-placeholder]:top-0 [&_.aui-lexical-placeholder]:left-0 [&_.aui-lexical-placeholder]:px-1.75 [&_.aui-lexical-placeholder]:py-1 [&_.aui-lexical-placeholder]:text-muted-foreground/80"
            />
            <ComposerAction />
          </div>
        </ComposerPrimitive.AttachmentDropzone>

        <ComposerTriggerPopover
          char="@"
          {...mention}
          emptyItemsLabel={t('chat.commands.mention.empty')}
        />

        <ComposerTriggerPopover
          char="/"
          {...slash}
          emptyItemsLabel={t('chat.commands.slash.empty')}
        />
      </ComposerPrimitive.Root>
    </ComposerPrimitive.Unstable_TriggerPopoverRoot>
  );
};

const ComposerAction: FC = () => {
  const { t } = useTranslation();
  return (
    <div className="aui-composer-action-wrapper relative flex items-center justify-between">
      <ComposerAddAttachment />
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerSendButton />
      </AuiIf>
      <AuiIf condition={(s) => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel size-8 rounded-full"
            aria-label={t('chat.composer.stopGenerating')}
          >
            <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
          </Button>
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

const MessageError: FC = () => {
  const { t } = useTranslation();
  // Render a clean, localized message rather than the transport's raw thrown
  // string. For `AssistantChatTransport` that string is `await response.text()`
  // of the failed POST — i.e. the raw `application/problem+json` envelope —
  // which is meaningless to end users. `MessagePrimitive.Error` already gates
  // rendering on `status === incomplete/error`, so this only shows on a real
  // stream error.
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <p className="aui-message-error-message line-clamp-2">{t('chat.error.generic')}</p>
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const AssistantMessage: FC = () => {
  // reserves space for action bar and compensates with `-mb` for consistent msg spacing
  // keeps hovered action bar from shifting layout (autohide doesn't support absolute positioning well)
  // for pt-[n] use -mb-[n + 6] & min-h-[n + 6] to preserve compensation
  const ACTION_BAR_PT = 'pt-1.5';
  const ACTION_BAR_HEIGHT = `-mb-7.5 min-h-7.5 ${ACTION_BAR_PT}`;

  return (
    <MessagePrimitive.Root
      data-slot="aui_assistant-message-root"
      data-role="assistant"
      className="fade-in slide-in-from-bottom-1 relative mx-auto w-full max-w-(--thread-max-width) animate-in duration-150"
    >
      <div
        data-slot="aui_assistant-message-content"
        className="wrap-break-word px-2 text-foreground leading-relaxed"
      >
        <MessagePrimitive.GroupedParts
          groupBy={(part) => {
            if (part.type === 'reasoning') return ['group-chainOfThought', 'group-reasoning'];
            if (part.type === 'tool-call') return ['group-chainOfThought', 'group-tool'];
            return null;
          }}
        >
          {({ part, children }) => {
            switch (part.type) {
              case 'group-chainOfThought':
                return <div data-slot="aui_chain-of-thought">{children}</div>;
              case 'group-reasoning': {
                const running = part.status.type === 'running';
                return (
                  <ReasoningRoot defaultOpen={running}>
                    <ReasoningTrigger active={running} />
                    <ReasoningContent aria-busy={running}>
                      <ReasoningText>{children}</ReasoningText>
                    </ReasoningContent>
                  </ReasoningRoot>
                );
              }
              case 'group-tool':
                return (
                  <ToolGroupRoot>
                    <ToolGroupTrigger
                      count={part.indices.length}
                      active={part.status.type === 'running'}
                    />
                    <ToolGroupContent>{children}</ToolGroupContent>
                  </ToolGroupRoot>
                );
              case 'text':
                return <MarkdownText />;
              case 'reasoning':
                return <Reasoning {...part} />;
              case 'tool-call':
                return part.toolUI ?? <ToolFallback {...part} />;
              case 'data':
                return part.dataRendererUI;
              default:
                return null;
            }
          }}
        </MessagePrimitive.GroupedParts>
        <MessageError />
        <CitationFooter />
      </div>

      <div
        data-slot="aui_assistant-message-footer"
        className={cn('ml-2 flex items-center', ACTION_BAR_HEIGHT)}
      >
        <BranchPicker />
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  const { t } = useTranslation();
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip={t('chat.actions.copy')}>
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip={t('chat.actions.refresh')}>
          <RefreshCwIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
      <ReportAnswerButton />
      <ActionBarMorePrimitive.Root>
        <ActionBarMorePrimitive.Trigger asChild>
          <TooltipIconButton
            tooltip={t('chat.actions.more')}
            className="data-[state=open]:bg-accent"
          >
            <MoreHorizontalIcon />
          </TooltipIconButton>
        </ActionBarMorePrimitive.Trigger>
        <ActionBarMorePrimitive.Content
          side="bottom"
          align="start"
          className="aui-action-bar-more-content z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <ActionBarPrimitive.ExportMarkdown asChild>
            <ActionBarMorePrimitive.Item className="aui-action-bar-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
              <DownloadIcon className="size-4" />
              {t('chat.actions.exportMarkdown')}
            </ActionBarMorePrimitive.Item>
          </ActionBarPrimitive.ExportMarkdown>
        </ActionBarMorePrimitive.Content>
      </ActionBarMorePrimitive.Root>
      <MessageTiming />
    </ActionBarPrimitive.Root>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      data-slot="aui_user-message-root"
      data-role="user"
      className="fade-in slide-in-from-bottom-1 mx-auto grid w-full max-w-(--thread-max-width) animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 duration-150"
    >
      <UserMessageAttachments />

      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div className="aui-user-message-content wrap-break-word peer rounded-2xl bg-muted px-4 py-2.5 text-foreground empty:hidden">
          <MessagePrimitive.Quote>{(quote) => <QuoteBlock {...quote} />}</MessagePrimitive.Quote>
          <MessagePrimitive.Parts components={{ Text: DirectiveText }} />
        </div>
        <div className="aui-user-action-bar-wrapper absolute top-1/2 left-0 -translate-x-full -translate-y-1/2 pr-2 peer-empty:hidden">
          <UserActionBar />
        </div>
      </div>

      <BranchPicker
        data-slot="aui_user-branch-picker"
        className="col-span-full col-start-1 row-start-3 -mr-1 justify-end"
      />
    </MessagePrimitive.Root>
  );
};

const ReportAnswerButton: FC = () => {
  const { t } = useTranslation();
  const messageId = useAuiState((s) => s.message.id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<'incorrect' | 'incomplete' | 'not_relevant' | 'other'>(
    'incorrect',
  );
  const [text, setText] = useState('');
  const [includeReporter, setIncludeReporter] = useState(false);
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');

  const submit = async () => {
    setStatus('pending');
    try {
      const res = await fetch(`${env.VITE_API_BASE_URL}/feedback/report`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          messageId,
          reason,
          ...(text.trim() ? { text: text.trim() } : {}),
          ...(includeReporter ? { includeReporter: true } : {}),
        }),
      });
      if (!res.ok) throw new Error(`feedback failed: ${res.status}`);
      setStatus('success');
      setText('');
      setIncludeReporter(false);
      setOpen(false);
    } catch {
      setStatus('error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <TooltipIconButton tooltip={t('chat.actions.reportAnswer')}>
          <TriangleAlertIcon />
        </TooltipIconButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('chat.report.title')}</DialogTitle>
          <DialogDescription>{t('chat.report.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="feedback-reason"
              value="incorrect"
              checked={reason === 'incorrect'}
              onChange={() => setReason('incorrect')}
            />
            {t('chat.report.reasons.incorrect')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="feedback-reason"
              value="incomplete"
              checked={reason === 'incomplete'}
              onChange={() => setReason('incomplete')}
            />
            {t('chat.report.reasons.incomplete')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="feedback-reason"
              value="not_relevant"
              checked={reason === 'not_relevant'}
              onChange={() => setReason('not_relevant')}
            />
            {t('chat.report.reasons.not_relevant')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="feedback-reason"
              value="other"
              checked={reason === 'other'}
              onChange={() => setReason('other')}
            />
            {t('chat.report.reasons.other')}
          </label>
          <Textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={2000}
            placeholder={t('chat.report.placeholder')}
          />
          <label className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3 text-sm">
            <input
              type="checkbox"
              checked={includeReporter}
              onChange={(event) => setIncludeReporter(event.target.checked)}
              className="mt-1"
            />
            <span className="flex flex-col gap-1">
              <span>{t('chat.report.includeReporter')}</span>
              <span className="text-muted-foreground text-xs">
                {t('chat.report.includeReporterHint')}
              </span>
            </span>
          </label>
          {status === 'error' ? (
            <p role="alert" className="text-sm text-destructive">
              {t('chat.report.error')}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            {t('chat.report.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={status === 'pending'}>
            {status === 'pending' ? t('chat.report.sending') : t('chat.report.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserActionBar: FC = () => {
  const { t } = useTranslation();
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip={t('chat.actions.edit')} className="aui-user-action-edit p-4">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  const { t } = useTranslation();
  return (
    <MessagePrimitive.Root
      data-slot="aui_edit-composer-wrapper"
      className="mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2"
    >
      <ComposerPrimitive.Unstable_TriggerPopoverRoot>
        <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
          <LexicalComposerInput
            directiveChip={DirectiveChip}
            autoFocus
            className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none [&_.aui-directive-chip-icon]:self-center [&_.aui-directive-chip]:inline-flex [&_.aui-directive-chip]:items-baseline [&_.aui-directive-chip]:gap-1 [&_.aui-directive-chip]:rounded-md [&_.aui-directive-chip]:bg-blue-100 [&_.aui-directive-chip]:px-1.5 [&_.aui-directive-chip]:py-0.5 [&_.aui-directive-chip]:font-medium [&_.aui-directive-chip]:text-[13px] [&_.aui-directive-chip]:text-blue-700 [&_.aui-directive-chip]:leading-none dark:[&_.aui-directive-chip]:bg-blue-900/50 dark:[&_.aui-directive-chip]:text-blue-300 [&_.aui-lexical-input]:min-h-lh [&_.aui-lexical-input]:outline-none"
          />
          <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
            <ComposerPrimitive.Cancel asChild>
              <Button variant="ghost" size="sm">
                {t('chat.composer.cancel')}
              </Button>
            </ComposerPrimitive.Cancel>
            <ComposerPrimitive.Send asChild>
              <Button size="sm">{t('chat.composer.update')}</Button>
            </ComposerPrimitive.Send>
          </div>
        </ComposerPrimitive.Root>
      </ComposerPrimitive.Unstable_TriggerPopoverRoot>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({ className, ...rest }) => {
  const { t } = useTranslation();
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        'aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-muted-foreground text-xs',
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={t('chat.actions.previous')}>
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={t('chat.actions.next')}>
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
